// features/profile/UserProfile.jsx
import React from 'react'
import { useLocation } from 'react-router-dom'
import {
  getMyProfileFS,          // ✅ read users/{auth.uid}
  userChangePasswordFS,
  upsertUserFS,
} from '@/services/firestore/usersService'

// ✅ Use the shared theme helpers used by main.jsx
import { getStoredTheme, setThemePreference } from '@/lib/theme'

const lc = s => (s || '').toString().trim().toLowerCase()
const useQuery = () => new URLSearchParams(useLocation().search)

/* ========== COMPONENT ========== */
export default function UserProfile() {
  const q = useQuery()
  const forceChange = q.get('forceChange') === '1'

  const [err, setErr] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)

  const [email, setEmail] = React.useState('')
  const [displayName, setDisplayName] = React.useState('')
  const [title, setTitle] = React.useState('') // locked (read-only)
  const [roles, setRoles] = React.useState([])
  const [communities, setCommunities] = React.useState([])
  const [requireChange, setRequireChange] = React.useState(false)
  const [microsoftOnly,setMicrosoftOnly]=React.useState(false)

  // password form
  const [curPwd, setCurPwd] = React.useState('')
  const [newPwd, setNewPwd] = React.useState('')
  const [confirmPwd, setConfirmPwd] = React.useState('')
  const [pwMsg, setPwMsg] = React.useState('')

  // appearance (use shared theme store so it persists across refresh)
  const [themeChoice, setThemeChoice] = React.useState(() => {
    const saved = getStoredTheme() // 'light' | 'dark' | 'system' | null
    return saved || 'system'
  })

  // Load profile (✅ by UID: users/{auth.uid})
  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setErr(''); setLoading(true)

        const me = await getMyProfileFS() // ← key call
        if (!alive) return
        if (!me) throw new Error('Profile not found.')

        setMicrosoftOnly(me.authMethod==='microsoft')
        setEmail(me.email || '')
        setDisplayName(me.displayName || '')
        setTitle(me.title || '')
        setRoles(Array.isArray(me.roles) ? me.roles : [])
        setCommunities(Array.isArray(me.communities) ? me.communities : [])
        setRequireChange(me.requirePasswordChange === true)
      } catch (e) {
        if (alive) setErr(e?.message || 'Missing or insufficient permissions.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  async function onSaveProfile() {
    if (saving || loading) return
    const nextName = (displayName || '').trim()
    if (nextName.length === 0) { setErr('Display name cannot be empty.'); return }
    if (nextName.length > 120) { setErr('Display name is too long.'); return }

    try {
      setErr(''); setSaving(true)
      await upsertUserFS({
        id: lc(email),
        email: lc(email),
        displayName: nextName,
        title, // unchanged, from server
        roles, communities, isActive: true,
      })
      // refresh session header
      const sess = JSON.parse(localStorage.getItem('session') || 'null') || {}
      const updated = { ...sess, displayName: nextName || sess.displayName, title }
      localStorage.setItem('session', JSON.stringify(updated))
      window.dispatchEvent(new Event('sessionchange'))
      alert('Profile saved.')
    } catch (e) {
      setErr(e?.message || 'Failed to save profile.')
    } finally {
      setSaving(false)
    }
  }

  async function onChangePassword(e) {
    e.preventDefault()
    setPwMsg('')

    if (!newPwd || newPwd.length < 6) { setPwMsg('Password must be at least 6 characters.'); return }
    if (newPwd !== confirmPwd) { setPwMsg('New password and confirmation do not match.'); return }

    try {
      await userChangePasswordFS(email, curPwd, newPwd)
      setPwMsg('Password updated. You can continue.')
      setCurPwd(''); setNewPwd(''); setConfirmPwd('')
      setRequireChange(false)

      const sess = JSON.parse(localStorage.getItem('session') || 'null') || {}
      localStorage.setItem('session', JSON.stringify({ ...sess, requirePasswordChange: false }))
      window.dispatchEvent(new Event('sessionchange'))
    } catch (e2) {
      setPwMsg(e2?.message || 'Failed to update password.')
    }
  }

  function onThemeChange(e) {
    const choice = e.target.value // 'light' | 'dark' | 'system'
    setThemeChoice(choice)
    setThemePreference(choice)
  }

  const showForceBanner = requireChange || forceChange

  return (
    <div>
      <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    marginBottom: 12,
  }}
>
  <span>My Profile</span>
</h2>

      {err && (
        <div style={{background:'#fdecec', color:'#a50000', padding:'10px 12px', border:'1px solid #e3b1b1', borderRadius:8, marginBottom:12}}>
          {err}
        </div>
      )}

      {showForceBanner && (
        <div className="card" style={{ borderColor:'#e0a800', background:'#fffaf0' }}>
          <strong>Action required:</strong> Your account is using a temporary password.
          Please set a new password below to continue using the app.
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="muted">Loading…</div>
        ) : (
          <div className="grid2" style={{ gap: 12 }}>
            <div>
              <label>Email (read-only)</label>
              <input value={email} readOnly />
            </div>

            <div>
              <label>Display Name</label>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Your name"
                disabled={showForceBanner || saving}
              />
            </div>

            <div>
              <label>Title (managed by admin)</label>
              <input
                value={title}
                readOnly
                disabled
                data-tooltip="Title is managed by an administrator and used for approval routing."
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Title is locked because approval routing depends on it.
              </div>
            </div>

            <div>
              <label>Roles</label>
              {roles?.length
                ? <div className="pillWrap">{roles.map(r => <span key={r} className="pill">{r}</span>)}</div>
                : 'None'}
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label>Communities</label>
              {communities?.length
                ? <div className="pillWrap">{communities.map(c => <span key={c} className="pill">{c}</span>)}</div>
                : 'None'}
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <button onClick={onSaveProfile} disabled={showForceBanner || saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <h3>Appearance</h3>
        <div role="radiogroup" aria-label="Theme" style={{ display:'flex', gap:16, marginTop:8, flexWrap:'wrap' }}>
          <label style={{ display:'inline-flex', alignItems:'center', gap:8, cursor:'pointer' }}>
            <input type="radio" name="uiTheme" value="light" checked={themeChoice === 'light'} onChange={onThemeChange}/>
            Day (Light)
          </label>
          <label style={{ display:'inline-flex', alignItems:'center', gap:8, cursor:'pointer' }}>
            <input type="radio" name="uiTheme" value="dark" checked={themeChoice === 'dark'} onChange={onThemeChange}/>
            Night (Dark)
          </label>
          <label style={{ display:'inline-flex', alignItems:'center', gap:8, cursor:'pointer' }}>
            <input type="radio" name="uiTheme" value="system" checked={themeChoice === 'system'} onChange={onThemeChange}/>
            System
          </label>
        </div>
        <p className="muted" style={{ marginTop:8 }}>
          Your choice is saved to this device and applied instantly. “System” follows your OS appearance and updates automatically.
        </p>
      </div>

      {microsoftOnly ? <div className="card" style={{marginTop:12}}><h3>Microsoft sign-in</h3><p>Your password and account recovery are managed by Microsoft or your IT team.</p></div> : <div className="card" style={{ marginTop: 12 }}>
        <h3>Change Password</h3>
        <form onSubmit={onChangePassword} style={{ marginTop: 8 }}>
          <div className="grid3" style={{ gap: 12 }}>
            <div>
              <label>Current Password</label>
              <input type="password" value={curPwd} onChange={e => setCurPwd(e.target.value)} required />
            </div>
            <div>
              <label>New Password</label>
              <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required />
            </div>
            <div>
              <label>Confirm New Password</label>
              <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required />
            </div>
          </div>

          {pwMsg && (
            <div className="alert" style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 8,
              background: pwMsg.startsWith('Password updated') ? 'rgba(47,143,91,.08)' : '#ffecec',
              color: pwMsg.startsWith('Password updated') ? '#1f6f49' : '#c00',
              border: `1px solid ${pwMsg.startsWith('Password updated') ? 'rgba(47,143,91,.35)' : '#f2b3b3'}`
            }}>
              {pwMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" className="primary">
              {showForceBanner ? 'Set New Password to Continue' : 'Update Password'}
            </button>
          </div>

          {showForceBanner && (
            <p className="muted" style={{ marginTop: 8 }}>
              You must change your password to continue.
            </p>
          )}
        </form>
      </div>}
    </div>
  )
}